import { create } from 'zustand';
import type { SongWithStats, Difficulty, VocalRangeLabel } from '../types/database';

export interface FilterState {
  difficulties: Difficulty[];
  vocalRangeLabels: VocalRangeLabel[];
  isDuet: boolean | null;
  genre: string | null;
  era: string | null;
  neverSung: boolean;
  notSungIn30Days: boolean;
}

interface SongsState {
  songs: SongWithStats[];
  isLoading: boolean;
  filters: FilterState;
  searchQuery: string;
}

interface SongsActions {
  setSongs: (songs: SongWithStats[]) => void;
  addSong: (song: SongWithStats) => void;
  updateSong: (id: string, updates: Partial<SongWithStats>) => void;
  removeSong: (id: string) => void;
  setFilters: (filters: Partial<FilterState>) => void;
  resetFilters: () => void;
  setSearch: (query: string) => void;
  setIsLoading: (isLoading: boolean) => void;
}

export type SongsStore = SongsState & SongsActions;

const initialFilters: FilterState = {
  difficulties: [],
  vocalRangeLabels: [],
  isDuet: null,
  genre: null,
  era: null,
  neverSung: false,
  notSungIn30Days: false,
};

export const useSongsStore = create<SongsStore>((set) => ({
  // State
  songs: [],
  isLoading: false,
  filters: initialFilters,
  searchQuery: '',

  // Actions
  setSongs: (songs) => set({ songs, isLoading: false }),

  addSong: (song) =>
    set((state) => ({
      songs: [song, ...state.songs],
    })),

  updateSong: (id, updates) =>
    set((state) => ({
      songs: state.songs.map((song) =>
        song.id === id ? { ...song, ...updates } : song
      ),
    })),

  removeSong: (id) =>
    set((state) => ({
      songs: state.songs.filter((song) => song.id !== id),
    })),

  setFilters: (filters) =>
    set((state) => ({
      filters: { ...state.filters, ...filters },
    })),

  resetFilters: () => set({ filters: initialFilters }),

  setSearch: (searchQuery) => set({ searchQuery }),

  setIsLoading: (isLoading) => set({ isLoading }),
}));

// Selector for filtered songs
export const selectFilteredSongs = (state: SongsStore): SongWithStats[] => {
  let filtered = state.songs;

  // Apply search filter
  if (state.searchQuery) {
    const query = state.searchQuery.toLowerCase();
    filtered = filtered.filter(
      (song) =>
        song.title.toLowerCase().includes(query) ||
        song.artist.toLowerCase().includes(query) ||
        song.album?.toLowerCase().includes(query)
    );
  }

  // Apply filters
  const { difficulties, vocalRangeLabels, isDuet, genre, era, neverSung, notSungIn30Days } = state.filters;

  if (difficulties.length > 0) {
    filtered = filtered.filter((song) => song.difficulty !== null && difficulties.includes(song.difficulty));
  }

  if (vocalRangeLabels.length > 0) {
    filtered = filtered.filter((song) => song.vocal_range_label !== null && vocalRangeLabels.includes(song.vocal_range_label));
  }

  if (isDuet !== null) {
    filtered = filtered.filter((song) => song.is_duet === isDuet);
  }

  if (genre !== null) {
    filtered = filtered.filter((song) => song.genre === genre);
  }

  if (era !== null) {
    filtered = filtered.filter((song) => {
      if (!song.release_year) return false;
      const decade = Math.floor(song.release_year / 10) * 10;
      return decade.toString() === era;
    });
  }

  if (neverSung) {
    filtered = filtered.filter((song) => song.times_performed === 0);
  }

  if (notSungIn30Days) {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    filtered = filtered.filter((song) => {
      if (!song.last_performed_at) return true;
      return new Date(song.last_performed_at) < thirtyDaysAgo;
    });
  }

  return filtered;
};

// Helper to check if any filters are active
export const hasActiveFilters = (filters: FilterState): boolean => {
  return (
    filters.difficulties.length > 0 ||
    filters.vocalRangeLabels.length > 0 ||
    filters.isDuet !== null ||
    filters.genre !== null ||
    filters.era !== null ||
    filters.neverSung ||
    filters.notSungIn30Days
  );
};

// Get count of active filters
export const getActiveFilterCount = (filters: FilterState): number => {
  let count = 0;
  if (filters.difficulties.length > 0) count += filters.difficulties.length;
  if (filters.vocalRangeLabels.length > 0) count += filters.vocalRangeLabels.length;
  if (filters.isDuet !== null) count += 1;
  if (filters.genre !== null) count += 1;
  if (filters.era !== null) count += 1;
  if (filters.neverSung) count += 1;
  if (filters.notSungIn30Days) count += 1;
  return count;
};
